
import { useEffect } from 'react';

export default function PrivacyPolicyPage() {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-slate-50 to-blue-50 py-20">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-blue-100 rounded-full mb-6">
            <i className="ri-shield-check-line text-3xl text-blue-600"></i>
          </div>
          <h1 className="text-4xl font-bold text-slate-900 mb-4">Privacy Policy</h1>
          <p className="text-lg text-slate-600 mb-2">
            Your privacy is important to us. This policy explains how we collect, use, and protect your personal data.
          </p>
          <p className="text-sm text-slate-500">
            Last Updated: January 15, 2025 | Effective Date: January 1, 2025
          </p>
        </div>
      </section>

      {/* Content Section */}
      <section className="py-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          
          {/* Introduction */}
          <div className="mb-12">
            <p className="text-slate-700 leading-relaxed mb-4">
              GlobalShip Logistics Ltd ("we", "us", "our") is committed to protecting and respecting your privacy. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you use our website and services. This policy complies with the General Data Protection Regulation (GDPR) and other applicable data protection laws.
            </p>
            <p className="text-slate-700 leading-relaxed">
              By using our services, you agree to the collection and use of information in accordance with this policy. If you do not agree with our policies and practices, please do not use our services.
            </p>
          </div>

          {/* 1. Data Controller Information */}
          <div className="mb-12">
            <div className="flex items-start mb-4">
              <div className="flex items-center justify-center w-10 h-10 bg-blue-100 rounded-lg mr-4 flex-shrink-0">
                <i className="ri-building-line text-xl text-blue-600"></i>
              </div>
              <div>
                <h2 className="text-2xl font-bold text-slate-900 mb-2">1. Data Controller Information</h2>
              </div>
            </div>
            <div className="pl-14">
              <div className="bg-blue-50 border-l-4 border-blue-600 p-6 rounded-r-lg mb-4">
                <p className="font-semibold text-slate-900 mb-2">Data Controller:</p>
                <p className="text-slate-700 mb-1">GlobalShip Logistics Ltd</p>
                <p className="text-slate-700 mb-1">123 Logistics Avenue, London, EC1A 1BB, United Kingdom</p>
                <p className="text-slate-700 mb-1">Company Registration Number: GB-12345678</p>
                <p className="text-slate-700 mb-1">VAT Number: GB987654321</p>
              </div>
              <div className="bg-slate-50 p-6 rounded-lg">
                <p className="font-semibold text-slate-900 mb-2">Data Protection Officer (DPO):</p>
                <p className="text-slate-700 mb-1">Email: dpo@globalship.com</p>
                <p className="text-slate-700 mb-1">Phone: +44 20 7123 4567</p>
                <p className="text-slate-700">Postal Address: Data Protection Officer, GlobalShip Logistics Ltd, 123 Logistics Avenue, London, EC1A 1BB, UK</p>
              </div>
            </div>
          </div>

          {/* 2. Types of Data Collected */}
          <div className="mb-12">
            <div className="flex items-start mb-4">
              <div className="flex items-center justify-center w-10 h-10 bg-green-100 rounded-lg mr-4 flex-shrink-0">
                <i className="ri-database-2-line text-xl text-green-600"></i>
              </div>
              <div>
                <h2 className="text-2xl font-bold text-slate-900 mb-2">2. Types of Data We Collect</h2>
              </div>
            </div>
            <div className="pl-14 space-y-6">
              <div>
                <h3 className="text-lg font-semibold text-slate-900 mb-3">2.1 Personal Identification Information</h3>
                <ul className="list-disc list-inside space-y-2 text-slate-700">
                  <li>Full name, email address, phone number</li>
                  <li>Postal address (billing and shipping addresses)</li>
                  <li>Date of birth (for age verification and customs purposes)</li>
                  <li>Government-issued ID numbers (passport, national ID for customs clearance)</li>
                  <li>Company name and business registration details (for business accounts)</li>
                </ul>
              </div>

              <div>
                <h3 className="text-lg font-semibold text-slate-900 mb-3">2.2 Financial Information</h3>
                <ul className="list-disc list-inside space-y-2 text-slate-700">
                  <li>Payment card details (processed securely through third-party payment processors)</li>
                  <li>Bank account information (for refunds and business accounts)</li>
                  <li>Billing history and transaction records</li>
                  <li>Tax identification numbers (for invoicing purposes)</li>
                </ul>
              </div>

              <div>
                <h3 className="text-lg font-semibold text-slate-900 mb-3">2.3 Shipment Information</h3>
                <ul className="list-disc list-inside space-y-2 text-slate-700">
                  <li>Sender and recipient details (names, addresses, contact information)</li>
                  <li>Package contents, weight, dimensions, and declared value</li>
                  <li>Customs declarations and documentation</li>
                  <li>Tracking numbers and shipment status updates</li>
                  <li>Delivery instructions and special handling requirements</li>
                </ul>
              </div>

              <div>
                <h3 className="text-lg font-semibold text-slate-900 mb-3">2.4 Technical and Usage Data</h3>
                <ul className="list-disc list-inside space-y-2 text-slate-700">
                  <li>IP address, browser type, and operating system</li>
                  <li>Device information (device type, unique device identifiers)</li>
                  <li>Pages visited, time spent on pages, and navigation paths</li>
                  <li>Referral sources and search terms used to find our website</li>
                  <li>Cookies and similar tracking technologies (see Section 7)</li>
                </ul>
              </div>

              <div>
                <h3 className="text-lg font-semibold text-slate-900 mb-3">2.5 Communication Data</h3>
                <ul className="list-disc list-inside space-y-2 text-slate-700">
                  <li>Email correspondence and customer support interactions</li>
                  <li>Phone call recordings (with prior notice and consent)</li>
                  <li>Live chat transcripts and feedback submissions</li>
                  <li>Survey responses and testimonials</li>
                </ul>
              </div>
            </div>
          </div>

          {/* 3. Purpose of Data Processing */}
          <div className="mb-12">
            <div className="flex items-start mb-4">
              <div className="flex items-center justify-center w-10 h-10 bg-purple-100 rounded-lg mr-4 flex-shrink-0">
                <i className="ri-file-list-3-line text-xl text-purple-600"></i>
              </div>
              <div>
                <h2 className="text-2xl font-bold text-slate-900 mb-2">3. Purpose of Data Processing</h2>
              </div>
            </div>
            <div className="pl-14">
              <p className="text-slate-700 mb-4">We collect and process your personal data for the following purposes:</p>
              <div className="space-y-4">
                <div className="bg-slate-50 p-5 rounded-lg">
                  <h4 className="font-semibold text-slate-900 mb-2">Service Delivery</h4>
                  <p className="text-slate-700">To process and fulfill your shipping orders, arrange pickups and deliveries, provide tracking information, and handle customs clearance procedures.</p>
                </div>
                <div className="bg-slate-50 p-5 rounded-lg">
                  <h4 className="font-semibold text-slate-900 mb-2">Account Management</h4>
                  <p className="text-slate-700">To create and manage your account, authenticate your identity, provide access to partner areas, and maintain your shipping history and preferences.</p>
                </div>
                <div className="bg-slate-50 p-5 rounded-lg">
                  <h4 className="font-semibold text-slate-900 mb-2">Payment Processing</h4>
                  <p className="text-slate-700">To process payments, issue invoices, handle refunds, detect and prevent fraud, and maintain financial records for accounting and tax purposes.</p>
                </div>
                <div className="bg-slate-50 p-5 rounded-lg">
                  <h4 className="font-semibold text-slate-900 mb-2">Customer Support</h4>
                  <p className="text-slate-700">To respond to your inquiries, resolve complaints, provide technical assistance, and improve our customer service quality.</p>
                </div>
                <div className="bg-slate-50 p-5 rounded-lg">
                  <h4 className="font-semibold text-slate-900 mb-2">Legal Compliance</h4>
                  <p className="text-slate-700">To comply with customs regulations, tax laws, anti-money laundering requirements, sanctions screening, and other legal obligations.</p>
                </div>
                <div className="bg-slate-50 p-5 rounded-lg">
                  <h4 className="font-semibold text-slate-900 mb-2">Marketing and Communications</h4>
                  <p className="text-slate-700">To send promotional offers, newsletters, service updates, and personalized recommendations (only with your consent, which you can withdraw at any time).</p>
                </div>
                <div className="bg-slate-50 p-5 rounded-lg">
                  <h4 className="font-semibold text-slate-900 mb-2">Service Improvement</h4>
                  <p className="text-slate-700">To analyze usage patterns, conduct research, develop new features, optimize routes, and enhance overall service quality.</p>
                </div>
                <div className="bg-slate-50 p-5 rounded-lg">
                  <h4 className="font-semibold text-slate-900 mb-2">Security and Fraud Prevention</h4>
                  <p className="text-slate-700">To protect against unauthorized access, detect suspicious activities, prevent fraud, and ensure the security of our systems and your data.</p>
                </div>
              </div>
            </div>
          </div>

          {/* 4. Legal Basis for Processing */}
          <div className="mb-12">
            <div className="flex items-start mb-4">
              <div className="flex items-center justify-center w-10 h-10 bg-amber-100 rounded-lg mr-4 flex-shrink-0">
                <i className="ri-scales-3-line text-xl text-amber-600"></i>
              </div>
              <div>
                <h2 className="text-2xl font-bold text-slate-900 mb-2">4. Legal Basis for Processing</h2>
              </div>
            </div>
            <div className="pl-14">
              <p className="text-slate-700 mb-4">Under GDPR, we process your personal data based on the following legal grounds:</p>
              <div className="space-y-3">
                <div className="flex items-start">
                  <div className="w-2 h-2 bg-blue-600 rounded-full mt-2 mr-3 flex-shrink-0"></div>
                  <div>
                    <p className="font-semibold text-slate-900">Contractual Necessity:</p>
                    <p className="text-slate-700">Processing is necessary to fulfill our contract with you (e.g., delivering your shipments, processing payments).</p>
                  </div>
                </div>
                <div className="flex items-start">
                  <div className="w-2 h-2 bg-blue-600 rounded-full mt-2 mr-3 flex-shrink-0"></div>
                  <div>
                    <p className="font-semibold text-slate-900">Legal Obligation:</p>
                    <p className="text-slate-700">Processing is required to comply with legal requirements (e.g., customs declarations, tax reporting, anti-money laundering checks).</p>
                  </div>
                </div>
                <div className="flex items-start">
                  <div className="w-2 h-2 bg-blue-600 rounded-full mt-2 mr-3 flex-shrink-0"></div>
                  <div>
                    <p className="font-semibold text-slate-900">Legitimate Interest:</p>
                    <p className="text-slate-700">Processing is necessary for our legitimate business interests (e.g., fraud prevention, service improvement, network security) while respecting your rights.</p>
                  </div>
                </div>
                <div className="flex items-start">
                  <div className="w-2 h-2 bg-blue-600 rounded-full mt-2 mr-3 flex-shrink-0"></div>
                  <div>
                    <p className="font-semibold text-slate-900">Consent:</p>
                    <p className="text-slate-700">For marketing communications and non-essential cookies, we process data only with your explicit consent, which you can withdraw at any time.</p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* 5. Data Retention Periods */}
          <div className="mb-12">
            <div className="flex items-start mb-4">
              <div className="flex items-center justify-center w-10 h-10 bg-red-100 rounded-lg mr-4 flex-shrink-0">
                <i className="ri-time-line text-xl text-red-600"></i>
              </div>
              <div>
                <h2 className="text-2xl font-bold text-slate-900 mb-2">5. Data Retention Periods</h2>
              </div>
            </div>
            <div className="pl-14">
              <p className="text-slate-700 mb-4">We retain your personal data only for as long as necessary to fulfill the purposes outlined in this policy:</p>
              <div className="overflow-x-auto">
                <table className="w-full border-collapse">
                  <thead>
                    <tr className="bg-slate-100">
                      <th className="border border-slate-300 px-4 py-3 text-left font-semibold text-slate-900">Data Type</th>
                      <th className="border border-slate-300 px-4 py-3 text-left font-semibold text-slate-900">Retention Period</th>
                      <th className="border border-slate-300 px-4 py-3 text-left font-semibold text-slate-900">Reason</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td className="border border-slate-300 px-4 py-3 text-slate-700">Account Information</td>
                      <td className="border border-slate-300 px-4 py-3 text-slate-700">Duration of account + 2 years</td>
                      <td className="border border-slate-300 px-4 py-3 text-slate-700">Legal claims, customer service</td>
                    </tr>
                    <tr className="bg-slate-50">
                      <td className="border border-slate-300 px-4 py-3 text-slate-700">Shipment Records</td>
                      <td className="border border-slate-300 px-4 py-3 text-slate-700">7 years after delivery</td>
                      <td className="border border-slate-300 px-4 py-3 text-slate-700">Legal compliance, claims</td>
                    </tr>
                    <tr>
                      <td className="border border-slate-300 px-4 py-3 text-slate-700">Financial Records</td>
                      <td className="border border-slate-300 px-4 py-3 text-slate-700">7 years</td>
                      <td className="border border-slate-300 px-4 py-3 text-slate-700">Tax and accounting laws</td>
                    </tr>
                    <tr className="bg-slate-50">
                      <td className="border border-slate-300 px-4 py-3 text-slate-700">Marketing Consent</td>
                      <td className="border border-slate-300 px-4 py-3 text-slate-700">Until consent withdrawn</td>
                      <td className="border border-slate-300 px-4 py-3 text-slate-700">Ongoing marketing activities</td>
                    </tr>
                    <tr>
                      <td className="border border-slate-300 px-4 py-3 text-slate-700">Customer Support</td>
                      <td className="border border-slate-300 px-4 py-3 text-slate-700">3 years after last contact</td>
                      <td className="border border-slate-300 px-4 py-3 text-slate-700">Service improvement, disputes</td>
                    </tr>
                    <tr className="bg-slate-50">
                      <td className="border border-slate-300 px-4 py-3 text-slate-700">Website Analytics</td>
                      <td className="border border-slate-300 px-4 py-3 text-slate-700">26 months</td>
                      <td className="border border-slate-300 px-4 py-3 text-slate-700">Usage analysis, optimization</td>
                    </tr>
                    <tr>
                      <td className="border border-slate-300 px-4 py-3 text-slate-700">CCTV Footage</td>
                      <td className="border border-slate-300 px-4 py-3 text-slate-700">30 days</td>
                      <td className="border border-slate-300 px-4 py-3 text-slate-700">Security, incident investigation</td>
                    </tr>
                  </tbody>
                </table>
              </div>
              <p className="text-slate-700 mt-4">After the retention period expires, we securely delete or anonymize your personal data. In some cases, we may retain data for longer periods if required by law or to defend legal claims.</p>
            </div>
          </div>

          {/* 6. Third-Party Sharing */}
          <div className="mb-12">
            <div className="flex items-start mb-4">
              <div className="flex items-center justify-center w-10 h-10 bg-indigo-100 rounded-lg mr-4 flex-shrink-0">
                <i className="ri-share-line text-xl text-indigo-600"></i>
              </div>
              <div>
                <h2 className="text-2xl font-bold text-slate-900 mb-2">6. Sharing Your Personal Data</h2>
              </div>
            </div>
            <div className="pl-14">
              <p className="text-slate-700 mb-4">We may share your personal data with the following categories of recipients:</p>
              <div className="space-y-4">
                <div className="border-l-4 border-blue-600 pl-4">
                  <h4 className="font-semibold text-slate-900 mb-2">Service Partners and Carriers</h4>
                  <p className="text-slate-700 mb-2">We share shipment information with airlines, shipping lines, trucking companies, and local delivery partners to facilitate the transportation and delivery of your packages.</p>
                  <p className="text-sm text-slate-600">Examples: DHL, FedEx, UPS, Maersk, local courier services</p>
                </div>

                <div className="border-l-4 border-green-600 pl-4">
                  <h4 className="font-semibold text-slate-900 mb-2">Customs and Government Authorities</h4>
                  <p className="text-slate-700 mb-2">We are legally required to share shipment details, customs declarations, and sender/recipient information with customs authorities and other government agencies for clearance and compliance purposes.</p>
                  <p className="text-sm text-slate-600">Examples: HM Revenue & Customs (UK), US Customs and Border Protection, EU customs authorities</p>
                </div>

                <div className="border-l-4 border-purple-600 pl-4">
                  <h4 className="font-semibold text-slate-900 mb-2">Payment Processors</h4>
                  <p className="text-slate-700 mb-2">We use secure third-party payment processors to handle credit card transactions and other payment methods. These processors are PCI-DSS compliant and do not store your full payment card details on our servers.</p>
                  <p className="text-sm text-slate-600">Examples: Stripe, PayPal, bank payment gateways</p>
                </div>

                <div className="border-l-4 border-amber-600 pl-4">
                  <h4 className="font-semibold text-slate-900 mb-2">Technology Service Providers</h4>
                  <p className="text-slate-700 mb-2">We work with technology companies that provide hosting, cloud storage, analytics, customer support tools, and other technical services necessary to operate our website and services.</p>
                  <p className="text-sm text-slate-600">Examples: Amazon Web Services (AWS), Google Analytics, customer support platforms</p>
                </div>

                <div className="border-l-4 border-red-600 pl-4">
                  <h4 className="font-semibold text-slate-900 mb-2">Professional Advisors</h4>
                  <p className="text-slate-700 mb-2">We may share data with lawyers, accountants, auditors, and other professional advisors when necessary for legal, financial, or business purposes.</p>
                </div>

                <div className="border-l-4 border-slate-600 pl-4">
                  <h4 className="font-semibold text-slate-900 mb-2">Business Transfers</h4>
                  <p className="text-slate-700 mb-2">In the event of a merger, acquisition, reorganization, or sale of assets, your personal data may be transferred to the acquiring entity, subject to the same privacy protections.</p>
                </div>

                <div className="border-l-4 border-orange-600 pl-4">
                  <h4 className="font-semibold text-slate-900 mb-2">Law Enforcement and Legal Compliance</h4>
                  <p className="text-slate-700 mb-2">We may disclose your data to law enforcement, regulatory authorities, or courts when required by law, to protect our rights, or to prevent fraud and illegal activities.</p>
                </div>
              </div>

              <div className="bg-amber-50 border border-amber-200 p-5 rounded-lg mt-6">
                <p className="font-semibold text-slate-900 mb-2">International Data Transfers</p>
                <p className="text-slate-700 mb-2">Some of our service providers are located outside the European Economic Area (EEA). When we transfer your data internationally, we ensure appropriate safeguards are in place:</p>
                <ul className="list-disc list-inside space-y-1 text-slate-700 ml-4">
                  <li>Standard Contractual Clauses (SCCs) approved by the European Commission</li>
                  <li>Adequacy decisions recognizing equivalent data protection standards</li>
                  <li>Binding Corporate Rules for intra-group transfers</li>
                  <li>Privacy Shield certification (where applicable)</li>
                </ul>
              </div>

              <p className="text-slate-700 mt-4">We do not sell, rent, or trade your personal data to third parties for their marketing purposes.</p>
            </div>
          </div>

          {/* 7. Cookies Policy */}
          <div className="mb-12">
            <div className="flex items-start mb-4">
              <div className="flex items-center justify-center w-10 h-10 bg-pink-100 rounded-lg mr-4 flex-shrink-0">
                <i className="ri-cookie-line text-xl text-pink-600"></i>
              </div>
              <div>
                <h2 className="text-2xl font-bold text-slate-900 mb-2">7. Cookies and Tracking Technologies</h2>
              </div>
            </div>
            <div className="pl-14">
              <p className="text-slate-700 mb-4">We use cookies and similar tracking technologies to enhance your experience on our website. A cookie is a small text file stored on your device that helps us recognize you and remember your preferences.</p>
              
              <h3 className="text-lg font-semibold text-slate-900 mb-3">Types of Cookies We Use:</h3>
              <div className="space-y-3 mb-6">
                <div className="bg-slate-50 p-4 rounded-lg">
                  <p className="font-semibold text-slate-900 mb-1">Essential Cookies (Always Active)</p>
                  <p className="text-slate-700 text-sm">Required for the website to function properly. These enable core functionality such as security, network management, and accessibility. You cannot opt out of these cookies.</p>
                  <p className="text-xs text-slate-600 mt-2">Examples: Session cookies, authentication tokens, load balancing</p>
                </div>

                <div className="bg-slate-50 p-4 rounded-lg">
                  <p className="font-semibold text-slate-900 mb-1">Functional Cookies</p>
                  <p className="text-slate-700 text-sm">Enable enhanced functionality and personalization, such as remembering your language preference, login details, and shipment tracking history.</p>
                  <p className="text-xs text-slate-600 mt-2">Examples: Language settings, user preferences, recently viewed items</p>
                </div>

                <div className="bg-slate-50 p-4 rounded-lg">
                  <p className="font-semibold text-slate-900 mb-1">Analytics Cookies</p>
                  <p className="text-slate-700 text-sm">Help us understand how visitors interact with our website by collecting and reporting information anonymously. This helps us improve our services.</p>
                  <p className="text-xs text-slate-600 mt-2">Examples: Google Analytics, page view tracking, bounce rate analysis</p>
                </div>

                <div className="bg-slate-50 p-4 rounded-lg">
                  <p className="font-semibold text-slate-900 mb-1">Marketing Cookies</p>
                  <p className="text-slate-700 text-sm">Used to track visitors across websites to display relevant advertisements and measure campaign effectiveness. These cookies are set by third-party advertising networks.</p>
                  <p className="text-xs text-slate-600 mt-2">Examples: Google Ads, Facebook Pixel, retargeting cookies</p>
                </div>
              </div>

              <h3 className="text-lg font-semibold text-slate-900 mb-3">Managing Your Cookie Preferences:</h3>
              <p className="text-slate-700 mb-3">You can control and manage cookies in several ways:</p>
              <ul className="list-disc list-inside space-y-2 text-slate-700 mb-4">
                <li>Use our cookie consent banner to accept or reject non-essential cookies</li>
                <li>Adjust your browser settings to block or delete cookies (note: this may affect website functionality)</li>
                <li>Opt out of Google Analytics by installing the Google Analytics Opt-out Browser Add-on</li>
                <li>Use privacy-focused browsers or browser extensions that block tracking cookies</li>
              </ul>

              <div className="bg-blue-50 border border-blue-200 p-5 rounded-lg">
                <p className="font-semibold text-slate-900 mb-2">Third-Party Cookies</p>
                <p className="text-slate-700">Some cookies are placed by third-party services that appear on our pages. We do not control these cookies. Please refer to the privacy policies of these third parties for more information about their cookies and how to opt out.</p>
              </div>
            </div>
          </div>

          {/* 8. Your Rights */}
          <div className="mb-12">
            <div className="flex items-start mb-4">
              <div className="flex items-center justify-center w-10 h-10 bg-teal-100 rounded-lg mr-4 flex-shrink-0">
                <i className="ri-user-settings-line text-xl text-teal-600"></i>
              </div>
              <div>
                <h2 className="text-2xl font-bold text-slate-900 mb-2">8. Your Data Protection Rights</h2>
              </div>
            </div>
            <div className="pl-14">
              <p className="text-slate-700 mb-4">Under GDPR and other data protection laws, you have the following rights regarding your personal data:</p>
              
              <div className="space-y-4">
                <div className="border border-slate-200 p-5 rounded-lg hover:shadow-md transition-shadow">
                  <div className="flex items-start">
                    <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center mr-3 flex-shrink-0 mt-1">
                      <i className="ri-eye-line text-blue-600"></i>
                    </div>
                    <div>
                      <h4 className="font-semibold text-slate-900 mb-2">Right of Access</h4>
                      <p className="text-slate-700">You have the right to request a copy of the personal data we hold about you. We will provide this information in a structured, commonly used, and machine-readable format within 30 days of your request.</p>
                    </div>
                  </div>
                </div>

                <div className="border border-slate-200 p-5 rounded-lg hover:shadow-md transition-shadow">
                  <div className="flex items-start">
                    <div className="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center mr-3 flex-shrink-0 mt-1">
                      <i className="ri-edit-line text-green-600"></i>
                    </div>
                    <div>
                      <h4 className="font-semibold text-slate-900 mb-2">Right to Rectification</h4>
                      <p className="text-slate-700">You can request that we correct any inaccurate or incomplete personal data. You can update most information directly through your account settings or by contacting our support team.</p>
                    </div>
                  </div>
                </div>

                <div className="border border-slate-200 p-5 rounded-lg hover:shadow-md transition-shadow">
                  <div className="flex items-start">
                    <div className="w-8 h-8 bg-red-100 rounded-full flex items-center justify-center mr-3 flex-shrink-0 mt-1">
                      <i className="ri-delete-bin-line text-red-600"></i>
                    </div>
                    <div>
                      <h4 className="font-semibold text-slate-900 mb-2">Right to Erasure (Right to be Forgotten)</h4>
                      <p className="text-slate-700">You can request that we delete your personal data in certain circumstances, such as when the data is no longer necessary for the purposes it was collected, or if you withdraw your consent. Note that we may need to retain some data for legal compliance.</p>
                    </div>
                  </div>
                </div>

                <div className="border border-slate-200 p-5 rounded-lg hover:shadow-md transition-shadow">
                  <div className="flex items-start">
                    <div className="w-8 h-8 bg-purple-100 rounded-full flex items-center justify-center mr-3 flex-shrink-0 mt-1">
                      <i className="ri-forbid-line text-purple-600"></i>
                    </div>
                    <div>
                      <h4 className="font-semibold text-slate-900 mb-2">Right to Restriction of Processing</h4>
                      <p className="text-slate-700">You can request that we limit the processing of your personal data in certain situations, such as when you contest the accuracy of the data or object to processing based on legitimate interests.</p>
                    </div>
                  </div>
                </div>

                <div className="border border-slate-200 p-5 rounded-lg hover:shadow-md transition-shadow">
                  <div className="flex items-start">
                    <div className="w-8 h-8 bg-amber-100 rounded-full flex items-center justify-center mr-3 flex-shrink-0 mt-1">
                      <i className="ri-download-line text-amber-600"></i>
                    </div>
                    <div>
                      <h4 className="font-semibold text-slate-900 mb-2">Right to Data Portability</h4>
                      <p className="text-slate-700">You have the right to receive your personal data in a structured, commonly used, and machine-readable format and to transmit that data to another controller without hindrance.</p>
                    </div>
                  </div>
                </div>

                <div className="border border-slate-200 p-5 rounded-lg hover:shadow-md transition-shadow">
                  <div className="flex items-start">
                    <div className="w-8 h-8 bg-orange-100 rounded-full flex items-center justify-center mr-3 flex-shrink-0 mt-1">
                      <i className="ri-close-circle-line text-orange-600"></i>
                    </div>
                    <div>
                      <h4 className="font-semibold text-slate-900 mb-2">Right to Object</h4>
                      <p className="text-slate-700">You can object to the processing of your personal data based on legitimate interests or for direct marketing purposes. We will stop processing unless we have compelling legitimate grounds that override your interests.</p>
                    </div>
                  </div>
                </div>

                <div className="border border-slate-200 p-5 rounded-lg hover:shadow-md transition-shadow">
                  <div className="flex items-start">
                    <div className="w-8 h-8 bg-indigo-100 rounded-full flex items-center justify-center mr-3 flex-shrink-0 mt-1">
                      <i className="ri-hand-coin-line text-indigo-600"></i>
                    </div>
                    <div>
                      <h4 className="font-semibold text-slate-900 mb-2">Right to Withdraw Consent</h4>
                      <p className="text-slate-700">Where we rely on your consent to process your personal data, you have the right to withdraw that consent at any time. This will not affect the lawfulness of processing based on consent before its withdrawal.</p>
                    </div>
                  </div>
                </div>

                <div className="border border-slate-200 p-5 rounded-lg hover:shadow-md transition-shadow">
                  <div className="flex items-start">
                    <div className="w-8 h-8 bg-pink-100 rounded-full flex items-center justify-center mr-3 flex-shrink-0 mt-1">
                      <i className="ri-file-shield-line text-pink-600"></i>
                    </div>
                    <div>
                      <h4 className="font-semibold text-slate-900 mb-2">Right to Lodge a Complaint</h4>
                      <p className="text-slate-700">You have the right to lodge a complaint with a supervisory authority, particularly in the EU member state of your habitual residence, place of work, or place of the alleged infringement if you believe we have violated your data protection rights.</p>
                      <p className="text-sm text-slate-600 mt-2">UK Supervisory Authority: Information Commissioner's Office (ICO) - ico.org.uk</p>
                    </div>
                  </div>
                </div>
              </div>

              <div className="bg-blue-50 border-l-4 border-blue-600 p-6 rounded-r-lg mt-6">
                <h4 className="font-semibold text-slate-900 mb-2">How to Exercise Your Rights</h4>
                <p className="text-slate-700 mb-3">To exercise any of these rights, please contact our Data Protection Officer:</p>
                <ul className="space-y-1 text-slate-700">
                  <li><strong>Email:</strong> dpo@globalship.com</li>
                  <li><strong>Phone:</strong> +44 20 7123 4567</li>
                  <li><strong>Online Form:</strong> Available in your account settings under "Privacy Requests"</li>
                </ul>
                <p className="text-slate-700 mt-3">We will respond to your request within 30 days. In some cases, we may need to verify your identity before processing your request to ensure the security of your personal data.</p>
              </div>
            </div>
          </div>

          {/* 9. Security Measures */}
          <div className="mb-12">
            <div className="flex items-start mb-4">
              <div className="flex items-center justify-center w-10 h-10 bg-cyan-100 rounded-lg mr-4 flex-shrink-0">
                <i className="ri-shield-check-line text-xl text-cyan-600"></i>
              </div>
              <div>
                <h2 className="text-2xl font-bold text-slate-900 mb-2">9. Data Security Measures</h2>
              </div>
            </div>
            <div className="pl-14">
              <p className="text-slate-700 mb-4">We implement comprehensive technical and organizational security measures to protect your personal data against unauthorized access, alteration, disclosure, or destruction:</p>
              
              <div className="grid md:grid-cols-2 gap-4 mb-6">
                <div className="bg-slate-50 p-5 rounded-lg">
                  <div className="flex items-center mb-3">
                    <i className="ri-lock-line text-2xl text-blue-600 mr-3"></i>
                    <h4 className="font-semibold text-slate-900">Encryption</h4>
                  </div>
                  <ul className="space-y-1 text-sm text-slate-700">
                    <li>• SSL/TLS encryption for data in transit</li>
                    <li>• AES-256 encryption for data at rest</li>
                    <li>• End-to-end encryption for sensitive communications</li>
                  </ul>
                </div>

                <div className="bg-slate-50 p-5 rounded-lg">
                  <div className="flex items-center mb-3">
                    <i className="ri-shield-keyhole-line text-2xl text-green-600 mr-3"></i>
                    <h4 className="font-semibold text-slate-900">Access Controls</h4>
                  </div>
                  <ul className="space-y-1 text-sm text-slate-700">
                    <li>• Multi-factor authentication (MFA)</li>
                    <li>• Role-based access control (RBAC)</li>
                    <li>• Regular access reviews and audits</li>
                  </ul>
                </div>

                <div className="bg-slate-50 p-5 rounded-lg">
                  <div className="flex items-center mb-3">
                    <i className="ri-server-line text-2xl text-purple-600 mr-3"></i>
                    <h4 className="font-semibold text-slate-900">Infrastructure Security</h4>
                  </div>
                  <ul className="space-y-1 text-sm text-slate-700">
                    <li>• Secure data centers with 24/7 monitoring</li>
                    <li>• Firewalls and intrusion detection systems</li>
                    <li>• Regular security patches and updates</li>
                  </ul>
                </div>

                <div className="bg-slate-50 p-5 rounded-lg">
                  <div className="flex items-center mb-3">
                    <i className="ri-eye-line text-2xl text-amber-600 mr-3"></i>
                    <h4 className="font-semibold text-slate-900">Monitoring & Response</h4>
                  </div>
                  <ul className="space-y-1 text-sm text-slate-700">
                    <li>• 24/7 security monitoring and logging</li>
                    <li>• Incident response procedures</li>
                    <li>• Regular security assessments and penetration testing</li>
                  </ul>
                </div>

                <div className="bg-slate-50 p-5 rounded-lg">
                  <div className="flex items-center mb-3">
                    <i className="ri-team-line text-2xl text-red-600 mr-3"></i>
                    <h4 className="font-semibold text-slate-900">Staff Training</h4>
                  </div>
                  <ul className="space-y-1 text-sm text-slate-700">
                    <li>• Regular security awareness training</li>
                    <li>• Confidentiality agreements</li>
                    <li>• Background checks for employees</li>
                  </ul>
                </div>

                <div className="bg-slate-50 p-5 rounded-lg">
                  <div className="flex items-center mb-3">
                    <i className="ri-file-shield-2-line text-2xl text-indigo-600 mr-3"></i>
                    <h4 className="font-semibold text-slate-900">Compliance & Audits</h4>
                  </div>
                  <ul className="space-y-1 text-sm text-slate-700">
                    <li>• ISO 27001 certified information security</li>
                    <li>• PCI-DSS compliance for payment processing</li>
                    <li>• Regular third-party security audits</li>
                  </ul>
                </div>
              </div>

              <div className="bg-amber-50 border border-amber-200 p-5 rounded-lg">
                <h4 className="font-semibold text-slate-900 mb-2">Data Breach Notification</h4>
                <p className="text-slate-700">In the unlikely event of a data breach that poses a risk to your rights and freedoms, we will notify you and the relevant supervisory authority within 72 hours of becoming aware of the breach, as required by GDPR. We will provide information about the nature of the breach, the likely consequences, and the measures taken to address it.</p>
              </div>

              <p className="text-slate-700 mt-4">While we implement robust security measures, no method of transmission over the internet or electronic storage is 100% secure. We cannot guarantee absolute security, but we continuously work to improve our security practices and protect your data.</p>
            </div>
          </div>

          {/* 10. Children's Privacy */}
          <div className="mb-12">
            <div className="flex items-start mb-4">
              <div className="flex items-center justify-center w-10 h-10 bg-rose-100 rounded-lg mr-4 flex-shrink-0">
                <i className="ri-parent-line text-xl text-rose-600"></i>
              </div>
              <div>
                <h2 className="text-2xl font-bold text-slate-900 mb-2">10. Children's Privacy</h2>
              </div>
            </div>
            <div className="pl-14">
              <p className="text-slate-700 mb-3">Our services are not directed to individuals under the age of 16. We do not knowingly collect personal data from children under 16 without parental consent.</p>
              <p className="text-slate-700 mb-3">If you are a parent or guardian and believe that your child has provided us with personal data without your consent, please contact us immediately at dpo@globalship.com. We will take steps to delete such information from our systems promptly.</p>
              <p className="text-slate-700">For business accounts where minors may be involved in shipments (e.g., school trips, youth organizations), we require that an adult with parental responsibility or legal authority provides consent and manages the account.</p>
            </div>
          </div>

          {/* 11. Changes to Privacy Policy */}
          <div className="mb-12">
            <div className="flex items-start mb-4">
              <div className="flex items-center justify-center w-10 h-10 bg-lime-100 rounded-lg mr-4 flex-shrink-0">
                <i className="ri-refresh-line text-xl text-lime-600"></i>
              </div>
              <div>
                <h2 className="text-2xl font-bold text-slate-900 mb-2">11. Changes to This Privacy Policy</h2>
              </div>
            </div>
            <div className="pl-14">
              <p className="text-slate-700 mb-3">We may update this Privacy Policy from time to time to reflect changes in our practices, legal requirements, or business operations. When we make material changes, we will:</p>
              <ul className="list-disc list-inside space-y-2 text-slate-700 mb-4">
                <li>Update the "Last Updated" date at the top of this policy</li>
                <li>Notify you via email if you have an account with us</li>
                <li>Display a prominent notice on our website for 30 days</li>
                <li>For significant changes affecting your rights, we may request your renewed consent</li>
              </ul>
              <p className="text-slate-700 mb-3">We encourage you to review this Privacy Policy periodically to stay informed about how we protect your personal data. Your continued use of our services after changes are posted constitutes your acceptance of the updated policy.</p>
              <p className="text-slate-700">Previous versions of this Privacy Policy are available upon request by contacting our Data Protection Officer.</p>
            </div>
          </div>

          {/* 12. Contact Information */}
          <div className="mb-12">
            <div className="flex items-start mb-4">
              <div className="flex items-center justify-center w-10 h-10 bg-sky-100 rounded-lg mr-4 flex-shrink-0">
                <i className="ri-mail-line text-xl text-sky-600"></i>
              </div>
              <div>
                <h2 className="text-2xl font-bold text-slate-900 mb-2">12. Contact Us</h2>
              </div>
            </div>
            <div className="pl-14">
              <p className="text-slate-700 mb-4">If you have any questions, concerns, or requests regarding this Privacy Policy or our data processing practices, please contact us:</p>
              
              <div className="grid md:grid-cols-2 gap-6">
                <div className="bg-blue-50 p-6 rounded-lg">
                  <h4 className="font-semibold text-slate-900 mb-3">Data Protection Officer</h4>
                  <div className="space-y-2 text-slate-700">
                    <div className="flex items-start">
                      <i className="ri-mail-line text-blue-600 mr-3 mt-1"></i>
                      <div>
                        <p className="text-sm text-slate-600">Email</p>
                        <p className="font-medium">dpo@globalship.com</p>
                      </div>
                    </div>
                    <div className="flex items-start">
                      <i className="ri-phone-line text-blue-600 mr-3 mt-1"></i>
                      <div>
                        <p className="text-sm text-slate-600">Phone</p>
                        <p className="font-medium">+44 20 7123 4567</p>
                      </div>
                    </div>
                    <div className="flex items-start">
                      <i className="ri-map-pin-line text-blue-600 mr-3 mt-1"></i>
                      <div>
                        <p className="text-sm text-slate-600">Postal Address</p>
                        <p className="font-medium">Data Protection Officer</p>
                        <p className="font-medium">GlobalShip Logistics Ltd</p>
                        <p className="font-medium">123 Logistics Avenue</p>
                        <p className="font-medium">London, EC1A 1BB</p>
                        <p className="font-medium">United Kingdom</p>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="bg-green-50 p-6 rounded-lg">
                  <h4 className="font-semibold text-slate-900 mb-3">General Inquiries</h4>
                  <div className="space-y-2 text-slate-700">
                    <div className="flex items-start">
                      <i className="ri-customer-service-line text-green-600 mr-3 mt-1"></i>
                      <div>
                        <p className="text-sm text-slate-600">Customer Support</p>
                        <p className="font-medium">support@globalship.com</p>
                      </div>
                    </div>
                    <div className="flex items-start">
                      <i className="ri-phone-line text-green-600 mr-3 mt-1"></i>
                      <div>
                        <p className="text-sm text-slate-600">24/7 Support Line</p>
                        <p className="font-medium">+44 800 123 4567</p>
                      </div>
                    </div>
                    <div className="flex items-start">
                      <i className="ri-time-line text-green-600 mr-3 mt-1"></i>
                      <div>
                        <p className="text-sm text-slate-600">Business Hours</p>
                        <p className="font-medium">Monday - Friday: 9:00 AM - 6:00 PM GMT</p>
                        <p className="font-medium">Saturday: 10:00 AM - 4:00 PM GMT</p>
                        <p className="font-medium">Sunday: Closed</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div className="bg-slate-100 p-6 rounded-lg mt-6">
                <h4 className="font-semibold text-slate-900 mb-2">Response Time</h4>
                <p className="text-slate-700">We aim to respond to all privacy-related inquiries within 5 business days. For formal data subject requests (access, deletion, etc.), we will respond within 30 days as required by GDPR.</p>
              </div>
            </div>
          </div>

          {/* Acknowledgment */}
          <div className="bg-gradient-to-r from-blue-50 to-green-50 border-l-4 border-blue-600 p-6 rounded-r-lg">
            <h3 className="text-lg font-semibold text-slate-900 mb-2">Acknowledgment</h3>
            <p className="text-slate-700">By using our services, you acknowledge that you have read and understood this Privacy Policy and agree to the collection, use, and disclosure of your personal data as described herein. If you do not agree with this policy, please discontinue use of our services.</p>
          </div>

        </div>
      </section>
    </div>
  );
}
